import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../../../hooks';
import {
  initialPurchaseRequestForm,
  PurchaseRequestForm,
  PurchaseRequestRecord,
  RequestLineRecord,
} from '../../../../../types/PurchaseRequest';
import {
  useDeletePRFile,
  useUploadPRFiles,
} from '../../../../../hooks/usePurchaseRequestHooks';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import { Resource } from '../../../../../lib/enums';
import { usePurchaseRequestStore } from '../../../../../store/PurchaseRequestStore';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import CustomForm from '../../../../../components/ui/CustomForm';
import PurchaseRequestLoadingView from '../../info/PurchaseRequestLoadingView';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import RequestLineEditPage from './RequestLineEditPage';

const PurchaseRequestFormEditPage = () => {
  const api = '/pr/requests';
  const params = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const purchaseRequestStore = usePurchaseRequestStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');

  const {
    mutate: uploadMutate,
    isError: isErrorUpload,
    error: errorUpload,
  } = useUploadPRFiles();
  const mutationDelete = useDeletePRFile();
  const {
    mutate,
    isPending,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(`${api}`);
  const { isLoading, data, isError, error } =
    useFetchData<PurchaseRequestRecord>(`${api}/${params.id}`);
  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });
  const { data: dataFiles } = useFetchFile(
    `${api}/${params.id}/files`,
    'api/pr/requests',
  );
  const fileUploads = dataFiles?.map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  useEffect(() => {
    if (data !== undefined) {
      data.requestLines.map((line: RequestLineRecord) => {
        line.uuid = uuidv4();
      });
      purchaseRequestStore.setFormData(data);
    }
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error?.message);
    }
  }, [data, isError]);

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpdate?.message);
    }
    if (isErrorUpload) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpload?.message);
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const payload: PurchaseRequestForm = {
      ...values,
      id: params.id,
      destination: destination,
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      buyer: buyerData?.data.find((b) => b.id === values.buyer),
      dateNeeded: values.dateNeeded
        ? dayjs(values.dateNeeded).format(dateFormat)
        : null,
      requestLines: purchaseRequestStore.formData.requestLines || [],
    };

    mutate({
      id: params.id,
      form: payload,
    });
    if (isSuccessUpdate) purchaseRequestStore.setFormData(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      uploadMutate({ id: params.id, formData: formData });
    }
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/purchase-request/' + params.id);
  };

  const handleCancel = () => {
    purchaseRequestStore.setFormData(initialPurchaseRequestForm);
    navigate('/purchase-request/' + params.id);
  };

  const handleDeleteFile = (file: any) => {
    mutationDelete.mutate(file.uid);
  };

  const { data: buyerData, isLoading: isBuyerLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/buyers', { resource: Resource.NAME, length: 999999 });

  const fields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      disabled: true,
      type: 'input',
      rules: [{ required: true, message: 'Please select code' }],
      defaultValue: data?.code,
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: data?.requiredDate,
    },
    {
      name: 'destination',
      label: 'Destination',
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue: data?.destination?.id,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: data?.source,
    },
    {
      name: 'status',
      label: 'Status',
      disabled: true,
      type: 'input',
      defaultValue: data?.status,
    },
    {
      name: 'requestedBy',
      label: 'Requested by',
      type: 'input',
      defaultValue: purchaseRequestStore.formData.requestedBy,
    },
    {
      name: 'buyer',
      label: 'Buyer',
      allowClear: true,
      isMultiple: false,
      type: 'select',
      isLoading: isBuyerLoading,
      values: buyerData?.data,
      defaultValue: purchaseRequestStore.formData.buyer?.id,
    },
    {
      name: 'dateNeeded',
      label: 'Date needed',
      type: 'datepicker',
      defaultValue: purchaseRequestStore.formData.dateNeeded,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      defaultValue: data?.notes,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
      defaultValue: fileUploads,
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Purchase Request'} />
        <Space>
          <Button onClick={handleCancel}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto">
        {isLoading ? (
          <div className="p-4">
            <PurchaseRequestLoadingView active={true} />
            <div className={'text-base font-semibold leading-6'}>
              Order Lines
            </div>
          </div>
        ) : (
          <div>
            <div className="p-4">
              <CustomForm
                onDeleteFile={handleDeleteFile}
                data={{ ...data, files: fileUploads }}
                formFields={fields}
                form={form}
                onFinish={onFinish}
                rowCol={3}
                layout={'vertical'}
                formName={'pr_form_update'}
              />
            </div>
            <RequestLineEditPage />
          </div>
        )}
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Purchase request successfully updated'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseRequestFormEditPage;
